/**
 * KaBoom Golf Co. — behaviour for the KaBoom home page sections.
 * Each piece is a small custom element so it works with the theme editor's
 * section reloads (elements re-connect when a section is re-rendered).
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * <kb-tabs>: product tabs ("Tee it up").
 * Buttons [role=tab] switch the matching [role=tabpanel]; arrow keys move between tabs.
 */
class KbTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const next = (index + step + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !selected;
    });
    const link = this.querySelector('[data-kb-tabs-link]');
    const active = this.tabs[index];
    if (link && active && active.dataset.shopUrl) link.href = active.dataset.shopUrl;
  }
}

/**
 * <kb-spotlight>: rotating product spotlight (Boom Golf feature).
 * Cycles slides on a timer, pauses on hover/focus, and thumbnails pick a slide.
 */
class KbSpotlight extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-kb-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-kb-thumb]'));
    this.frame = this.querySelector('[data-kb-frame]');
    this.name = this.querySelector('[data-kb-name]');
    this.nick = this.querySelector('[data-kb-nick]');
    this.price = this.querySelector('[data-kb-price]');
    this.index = 0;
    this.paused = false;

    this.thumbs.forEach((thumb, i) =>
      thumb.addEventListener('click', () => {
        this.userPicked = true;
        this.show(i);
      })
    );

    this.addEventListener('mouseenter', () => (this.paused = true));
    this.addEventListener('mouseleave', () => (this.paused = this.userPicked === true));
    this.addEventListener('focusin', () => (this.paused = true));

    const delay = Number(this.dataset.interval || 3200);
    if (this.slides.length > 1 && !reducedMotion()) {
      this.timer = window.setInterval(() => {
        if (!this.paused && !document.hidden) this.show((this.index + 1) % this.slides.length);
      }, delay);
    }
    this.show(0);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    const slide = this.slides[index];
    if (!slide) return;
    if (this.frame && slide.dataset.color) this.frame.style.setProperty('--kb-spot-color', slide.dataset.color);
    if (this.name) this.name.textContent = slide.dataset.name || '';
    if (this.nick) {
      this.nick.textContent = slide.dataset.nick ? `— ${slide.dataset.nick}` : '';
    }
    if (this.price) this.price.textContent = slide.dataset.price || '';
    this.slides.forEach((s, i) => {
      const link = s.querySelector('a');
      if (link) link.tabIndex = i === index ? 0 : -1;
    });
  }
}

/**
 * <kb-reviews>: previous/next buttons scroll the review track by one page.
 */
class KbReviews extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-kb-track]');
    if (!track) return;
    this.querySelectorAll('[data-kb-dir]').forEach((button) =>
      button.addEventListener('click', () => {
        const dir = Number(button.getAttribute('data-kb-dir'));
        const max = track.scrollWidth - track.clientWidth;
        let left = track.scrollLeft + dir * track.clientWidth;
        if (left > max + 4) left = 0;
        if (left < -4) left = max;
        track.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      })
    );
  }
}

/**
 * <kb-look-form>: adds every product in "Shop the look" to the cart in one request,
 * then takes the shopper to the cart. Without JavaScript the form posts natively.
 */
class KbLookForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-kb-error]');
    this.form?.addEventListener('submit', (event) => this.submit(event));
    this.querySelectorAll('select[data-kb-variant]').forEach((select) =>
      select.addEventListener('change', () => this.updateTotal())
    );
  }

  updateTotal() {
    const total = this.querySelector('[data-kb-total]');
    if (!total) return;
    let cents = 0;
    this.querySelectorAll('[data-kb-line]').forEach((line) => {
      const select = line.querySelector('select[data-kb-variant]');
      const option = select ? select.selectedOptions[0] : null;
      cents += Number(option ? option.dataset.price : line.dataset.price) || 0;
    });
    const format = total.dataset.format;
    if (format) total.textContent = formatMoney(cents, format);
  }

  async submit(event) {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const items = Array.from(this.form.querySelectorAll('[data-kb-line]'))
      .map((line) => {
        const field = line.querySelector('[name$="[id]"]');
        return field && field.value ? { id: Number(field.value), quantity: 1 } : null;
      })
      .filter(Boolean);
    if (!items.length) return;

    button?.setAttribute('disabled', '');
    if (this.error) this.error.hidden = true;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add the look');
      }
      window.location.href = this.form.dataset.cartUrl || `${root}cart`;
    } catch (error) {
      if (this.error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
      }
      button?.removeAttribute('disabled');
    }
  }
}

/** Minimal Shopify money formatter for the look total. */
function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [whole, decimals] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withCommas;
      case 'amount_with_comma_separator':
        return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
      case 'amount_no_decimals_with_comma_separator':
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      default:
        return `${withCommas}.${decimals}`;
    }
  });
}

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('kb-tabs', KbTabs);
define('kb-spotlight', KbSpotlight);
define('kb-reviews', KbReviews);
define('kb-look-form', KbLookForm);

/* ==========================================================================
   Page templates
   ========================================================================== */

const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const DEFAULT_FORMAT = '${{amount}}';

async function addItemsToCart(items) {
  const response = await fetch(`${cartRoot()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.description || data.message || 'Could not add to cart');
  }
  return response.json();
}

/**
 * <kb-product>: variant selection, quantity, personalization, size guide and sticky bar.
 * The form posts natively to /cart/add, so the page works without JavaScript too.
 */
class KbProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-kb-variants]');
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.format = this.dataset.moneyFormat || DEFAULT_FORMAT;
    this.form = this.querySelector('[data-kb-form]') || this.querySelector('form');
    this.idInput = this.querySelector('[data-kb-variant-id]');
    this.addButton = this.querySelector('[data-kb-add]');
    this.fieldsets = Array.from(this.querySelectorAll('[data-kb-option]'));

    this.fieldsets.forEach((fs) => fs.addEventListener('change', () => this.onOptionChange()));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.setupQuantity();
    this.setupPersonalize();
    this.setupDialogs();
    this.setupFinder();
    this.setupSticky();
    this.updateAvailability();
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  currentVariant() {
    const id = Number(this.idInput && this.idInput.value);
    return this.variants.find((v) => v.id === id);
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
    this.fieldsets.forEach((fs, i) => {
      const label = fs.querySelector('[data-kb-option-label]');
      if (label) label.textContent = selected[i] || '';
    });
    this.setVariant(variant);
  }

  setVariant(variant) {
    const priceRow = this.querySelector('[data-kb-price]');
    const price = priceRow && priceRow.querySelector('.kb-price');
    if (!variant) {
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Unavailable';
      }
      return;
    }
    this.idInput.value = variant.id;
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (price) {
      price.classList.toggle('kb-price--on-sale', !!onSale);
      price.innerHTML =
        `<span class="kb-price__now">${formatMoney(variant.price, this.format)}</span>` +
        (onSale ? `<s class="kb-price__was">${formatMoney(variant.compare_at_price, this.format)}</s>` : '');
    }
    if (priceRow) {
      const save = priceRow.querySelector('.kb-pdp__save');
      if (save) save.remove();
      if (onSale) {
        const tag = document.createElement('span');
        tag.className = 'kb-pdp__save';
        tag.textContent = `Save ${formatMoney(variant.compare_at_price - variant.price, this.format)}`;
        priceRow.appendChild(tag);
      }
    }
    this.updateButton();
    this.updateAvailability();

    if (variant.featured_media) {
      const gallery = this.querySelector('[data-kb-gallery]');
      const item = gallery && gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (item && gallery.firstElementChild !== item) gallery.prepend(item);
      if (gallery) gallery.scrollTo({ left: 0, behavior: 'smooth' });
    }

    const sticky = this.querySelector('[data-kb-sticky-select]');
    if (sticky) sticky.value = String(variant.id);
    const stickyPrice = this.querySelector('[data-kb-sticky-price] .kb-price__now');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, this.format);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    this.dispatchEvent(new CustomEvent('kb:variant-change', { bubbles: true, detail: { variant } }));
  }

  updateButton() {
    const variant = this.currentVariant();
    if (!this.addButton || !variant) return;
    const qtyInput = this.querySelector('[data-kb-qty-input]');
    const qty = Number(qtyInput && qtyInput.value) || 1;
    const extra = this.personalization()?.price || 0;
    this.addButton.disabled = !variant.available;
    this.addButton.textContent = variant.available
      ? `Add to cart — ${formatMoney((variant.price + extra) * qty, this.format)}`
      : 'Sold out';
  }

  /**
   * The personalization picked on the form, or null. `addonId` is the hidden charge product
   * added next to the polo; `price` is its price in cents.
   */
  personalization() {
    const box = this.querySelector('[data-kb-personalize]');
    const toggle = box && box.querySelector('[data-kb-personalize-toggle]');
    if (!toggle || !toggle.checked) return null;
    const properties = {};
    box.querySelectorAll('[name^="properties["]').forEach((field) => {
      const value = field.value.trim();
      if (value) properties[field.name.slice(11, -1)] = value;
    });
    return {
      properties,
      addonId: Number(box.dataset.kbPersonalizeVariant) || null,
      price: Number(box.dataset.kbPersonalizePrice) || 0,
      leadTime: box.dataset.kbPersonalizeLead || '',
    };
  }

  /** With a personalization charge, add the polo and the charge together; otherwise post natively. */
  async onSubmit(event) {
    const picked = this.personalization();
    const variant = this.currentVariant();
    if (!picked || !picked.addonId || !variant) return;
    event.preventDefault();
    const qtyInput = this.querySelector('[data-kb-qty-input]');
    const quantity = Number(qtyInput && qtyInput.value) || 1;
    const error = this.querySelector('[data-kb-add-error]');
    const forLabel = [(this.dataset.title || '').trim(), variant.title].filter(Boolean).join(' – ');
    if (error) error.hidden = true;
    if (this.addButton) this.addButton.disabled = true;
    try {
      await addItemsToCart([
        { id: variant.id, quantity, properties: picked.properties },
        {
          id: picked.addonId,
          quantity,
          properties: { For: forLabel, ...picked.properties, ...(picked.leadTime ? { 'Ships in': picked.leadTime } : {}) },
        },
      ]);
      window.location.href = `${cartRoot()}cart`;
    } catch (e) {
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
      if (this.addButton) this.addButton.disabled = false;
    }
  }

  /** Mark option values that have no available variant given the other selections. */
  updateAvailability() {
    const selected = this.selectedOptions();
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const probe = selected.slice();
        probe[i] = input.value;
        const match = this.variants.find((v) => v.options.every((opt, j) => opt === probe[j]));
        const box = input.nextElementSibling;
        if (box && box.classList.contains('kb-size')) box.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  setupQuantity() {
    const input = this.querySelector('[data-kb-qty-input]');
    if (!input) return;
    this.querySelectorAll('[data-kb-qty]').forEach((button) =>
      button.addEventListener('click', () => {
        const next = Math.min(99, Math.max(1, (Number(input.value) || 1) + Number(button.dataset.kbQty)));
        input.value = next;
        this.updateButton();
      })
    );
    input.addEventListener('change', () => this.updateButton());
  }

  setupPersonalize() {
    const toggle = this.querySelector('[data-kb-personalize-toggle]');
    const fields = this.querySelector('[data-kb-personalize-fields]');
    if (!toggle || !fields) return;
    const text = fields.querySelector('input[type="text"]');
    toggle.addEventListener('change', () => {
      fields.hidden = !toggle.checked;
      fields.querySelectorAll('input, select').forEach((el) => (el.disabled = !toggle.checked));
      if (text) text.required = toggle.checked;
      const lead = this.querySelector('[data-kb-personalize-lead-note]');
      if (lead) lead.hidden = !toggle.checked;
      // Buy-now buttons skip the cart, so they would drop the personalization charge.
      const express = this.querySelector('.shopify-payment-button');
      if (express) express.style.display = toggle.checked ? 'none' : '';
      this.updateButton();
    });
  }

  setupDialogs() {
    this.querySelectorAll('[data-kb-open]').forEach((button) => {
      const dialog = this.querySelector(`#${button.dataset.kbOpen}`);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      button.addEventListener('click', () => dialog.showModal());
      dialog.querySelectorAll('[data-kb-close]').forEach((close) => close.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  setupFinder() {
    const finder = this.querySelector('[data-kb-finder]');
    if (!finder) return;
    const sizes = (finder.dataset.sizes || '').split(',').map((x) => x.trim()).filter(Boolean);
    const height = finder.querySelector('[data-kb-height]');
    const fit = finder.querySelector('[data-kb-fit]');
    const outputs = finder.querySelectorAll('[data-kb-rec]');
    const recommend = () => {
      const index = Math.max(0, Math.min(sizes.length - 1, Number(height.value) + Number(fit.value)));
      outputs.forEach((o) => (o.textContent = sizes[index] || ''));
      return sizes[index];
    };
    height.addEventListener('change', recommend);
    fit.addEventListener('change', recommend);
    recommend();
    const use = finder.querySelector('[data-kb-use-rec]');
    if (use)
      use.addEventListener('click', () => {
        const size = recommend();
        const radio = Array.from(this.querySelectorAll('[data-kb-option] input')).find((r) => r.value === size);
        if (radio) {
          radio.checked = true;
          this.onOptionChange();
        }
        const dialog = finder.closest('dialog');
        if (dialog) dialog.close();
      });
  }

  setupSticky() {
    const bar = this.querySelector('[data-kb-sticky]');
    const buy = this.querySelector('[data-kb-buy]');
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelectorAll('button, select').forEach((el) => (el.tabIndex = show ? 0 : -1));
    });
    observer.observe(buy);
    const select = bar.querySelector('[data-kb-sticky-select]');
    if (select)
      select.addEventListener('change', () => {
        const variant = this.variants.find((v) => v.id === Number(select.value));
        if (!variant) return;
        this.fieldsets.forEach((fs, i) => {
          const radio = Array.from(fs.querySelectorAll('input')).find((r) => r.value === variant.options[i]);
          if (radio) radio.checked = true;
        });
        this.onOptionChange();
      });
    const add = bar.querySelector('[data-kb-sticky-add]');
    if (add && this.form) add.addEventListener('click', () => this.form.requestSubmit(this.addButton || undefined));
  }
}

/**
 * <kb-bundle>: "Complete the look". Checked items are added in one request.
 * The main product follows the variant picked in the product form.
 */
class KbBundle extends HTMLElement {
  connectedCallback() {
    this.format = this.dataset.moneyFormat || DEFAULT_FORMAT;
    this.threshold = Number(this.dataset.threshold) || 0;
    this.addEventListener('change', () => this.update());
    this.onVariant = (event) => {
      const main = this.querySelector('[data-kb-main]');
      if (!main) return;
      main.dataset.price = event.detail.variant.price;
      const price = main.querySelector('[data-kb-main-price] .kb-price__now');
      if (price) price.textContent = formatMoney(event.detail.variant.price, this.format);
      this.update();
    };
    document.addEventListener('kb:variant-change', this.onVariant);
    const button = this.querySelector('[data-kb-bundle-add]');
    if (button) button.addEventListener('click', () => this.add(button));
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('kb:variant-change', this.onVariant);
  }

  lines() {
    return Array.from(this.querySelectorAll('[data-kb-item]')).map((item) => {
      const toggle = item.querySelector('[data-kb-item-toggle]');
      const on = toggle ? toggle.checked : true;
      item.classList.toggle('is-off', !on);
      let id;
      let price = Number(item.dataset.price) || 0;
      if (item.hasAttribute('data-kb-main')) {
        const input = document.querySelector('[data-kb-variant-id]');
        id = input ? Number(input.value) : null;
      } else {
        const field = item.querySelector('[data-kb-item-variant]');
        id = field ? Number(field.value) : null;
        const option = field && field.selectedOptions ? field.selectedOptions[0] : null;
        if (option && option.dataset.price) price = Number(option.dataset.price);
      }
      return { on, id, price };
    });
  }

  update() {
    const picked = this.lines().filter((l) => l.on);
    const total = picked.reduce((sum, l) => sum + l.price, 0);
    const count = this.querySelector('[data-kb-count]');
    const totalEl = this.querySelector('[data-kb-total]');
    const ship = this.querySelector('[data-kb-ship]');
    if (count) count.textContent = `${picked.length} item${picked.length === 1 ? '' : 's'} selected`;
    if (totalEl) totalEl.textContent = formatMoney(total, this.format);
    if (ship && this.threshold) {
      ship.textContent =
        total >= this.threshold
          ? 'Ships free.'
          : `Add ${formatMoney(this.threshold - total, this.format)} more for free shipping.`;
    }
    const button = this.querySelector('[data-kb-bundle-add]');
    if (button) button.disabled = picked.length === 0;
  }

  async add(button) {
    const error = this.querySelector('[data-kb-error]');
    const items = this.lines()
      .filter((l) => l.on && l.id)
      .map((l) => ({ id: l.id, quantity: 1 }));
    if (!items.length) return;
    button.disabled = true;
    if (error) error.hidden = true;
    try {
      await addItemsToCart(items);
      window.location.href = this.dataset.cartUrl || `${cartRoot()}cart`;
    } catch (e) {
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
      button.disabled = false;
    }
  }
}

/** <kb-recommendations>: loads Shopify product recommendations into the section. */
class KbRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length || !this.dataset.url) return;
    const load = () =>
      fetch(this.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('kb-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    } else load();
  }
}

define('kb-product', KbProduct);
define('kb-bundle', KbBundle);
define('kb-recommendations', KbRecommendations);

/* Small global behaviours ------------------------------------------------- */

// Buttons with data-kb-toggle="id" open and close the element with that id (mobile filters).
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-kb-toggle]');
  if (!button) return;
  const target = document.getElementById(button.dataset.kbToggle);
  if (!target) return;
  const open = target.classList.toggle('is-open');
  button.setAttribute('aria-expanded', String(open));
});

// Forms marked data-kb-autosubmit submit as soon as a field changes (sort menu).
document.addEventListener('change', (event) => {
  const form = event.target.closest('form[data-kb-autosubmit]');
  if (form) form.submit();
});

// Quote form: pre-select fields from the link, for example ?occasion=Club%20%2F%20league or ?kind=hat
(() => {
  const params = new URLSearchParams(window.location.search);
  const apply = () => {
    document.querySelectorAll('select[data-kb-param]').forEach((select) => {
      let value = params.get(select.dataset.kbParam);
      if (!value && select.dataset.kbParam === 'product' && params.get('kind') === 'hat') value = 'BOOM Hat (custom logo)';
      if (!value) return;
      const option = Array.from(select.options).find((o) => o.value === value || o.text === value);
      if (option) select.value = option.value;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  else apply();
})();

// Quick add on product cards: add the chosen variant, then go to the cart.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-kb-quick-add]');
  if (!button) return;
  event.preventDefault();
  const bar = button.closest('.kb-quick__bar') || button;
  button.disabled = true;
  try {
    await addItemsToCart([{ id: Number(button.dataset.kbQuickAdd), quantity: 1 }]);
    bar.classList.add('is-added');
    bar.querySelector('.kb-quick__label')
      ? (bar.querySelector('.kb-quick__label').textContent = 'Added')
      : (button.textContent = 'Added');
    window.location.href = `${cartRoot()}cart`;
  } catch (e) {
    button.disabled = false;
    window.alert(e.message);
  }
});
